import * as React from "react";
import { css } from "@emotion/core";
import { ProcessedGeometry } from "components/Orders/state/types";
import { Button, Tag } from "@blueprintjs/core";
import { useUploadMachineSelector } from "../../context/upload-machine";
import { generateUniqueFileIdentifier } from "../../state/uploadMachine";
import { FileUploadActor } from "../../state/uploadMachine/types";
import { GeometryMachineContext } from "../../state/geometryMachine";
import { EditableLabel } from "./EditableLabel";
import { UploadStatusIcon } from "./UploadStatusIcon";

interface BaseGeometryUploadProps {
  upload: ProcessedGeometry;
  onClick?: () => void;
  onLabelChange?: (id: string, label: string) => void;
  children: (upload: ProcessedGeometry) => React.ReactNode; // renderTags function
  editable?: boolean;
}

export function BaseGeometryUpload({
  upload,
  onClick,
  onLabelChange,
  children,
  editable = true,
}: BaseGeometryUploadProps) {
  const geometryMachine = GeometryMachineContext.useActorRef();

  const handleLabelChange = (label: string) => {
    if (onLabelChange) {
      onLabelChange(upload.id, label);
    }
  };

  const completed = useUploadMachineSelector((state) => {
    return Object.values(upload.files).every((file) => {
      if (!file) return true;
      const fileIdentifier = generateUniqueFileIdentifier(file);
      return state.context.uploadedFiles.includes(fileIdentifier.name);
    });
  });

  const active = useUploadMachineSelector((state) => {
    return Object.values(upload.files).some((file) => {
      if (!file) return false;
      const fileIdentifier = generateUniqueFileIdentifier(file);
      return state.context.fileUploadActors.some((actor: FileUploadActor) => {
        return actor.id === `file-${fileIdentifier.name}`;
      });
    });
  });

  const uploadInProgress = useUploadMachineSelector((state) => {
    return state.context.fileUploadActors.length > 0;
  });

  const idle = GeometryMachineContext.useSelector((state) =>
    state.matches("idle")
  );

  const handleRemove = () => {
    geometryMachine.send({ type: "REMOVE_FILE", id: upload.id });
  };

  return (
    <div
      css={css`
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: start;
        gap: 0.25rem;
      `}
    >
      <div
        css={css`
          border: 1px solid var(--neutral-6);
        `}
      >
        <UploadStatusIcon
          completed={completed}
          active={active}
          uploadInProgress={uploadInProgress}
          fileAvailable={!upload.missing}
          type="geometry"
          onClick={onClick}
        />
      </div>
      <div
        css={css`
          flex-grow: 1;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 0.25rem 0.25rem 0.25rem 0.5rem;
          border: 1px solid var(--neutral-5);
          background-color: var(--neutral-6-translucent);
          cursor: ${onClick && upload.data?.bbox ? "pointer" : "default"};
          transition: background-color 0.2s ease;

          &:hover {
            background-color: ${onClick && upload.data?.bbox
              ? "var(--neutral-6)"
              : "var(--neutral-6-translucent)"};
          }
        `}
        onClick={onClick && upload.data?.bbox ? onClick : undefined}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 0.5rem;
          `}
        >
          {editable ? (
            <EditableLabel
              value={upload.name || ""}
              onSave={handleLabelChange}
            />
          ) : (
            <span>{upload.name || ""}</span>
          )}
          {(upload as any).status === "uploaded" && (
            <Tag
              intent={
                (upload as any).status === "uploaded" ? "success" : "warning"
              }
              minimal
            >
              {(upload as any).status === "uploaded"
                ? "Uploaded"
                : "Incomplete"}
            </Tag>
          )}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
            align-items: start;
          `}
        >
          {children(upload)}
          <Button
            icon="trash"
            intent="danger"
            disabled={idle === false || uploadInProgress}
            onClick={(event) => {
              event.stopPropagation();
              handleRemove();
            }}
          />
        </div>
      </div>
    </div>
  );
}
